import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import DocumentTypeVersionRollback from "./DocumentTypeVersionRollback";
import { DocumentTypeResponse } from "../../types/documentTypes";

const version = (
  id: number,
  versionNumber: number,
  status: "Draft" | "Published",
): DocumentTypeResponse => ({
  DocumentTypeID: id,
  Name: "Test document type",
  Description: null,
  CustomInstructions: null,
  Version: versionNumber,
  BaseDocumentTypeID: 1,
  Status: status,
  IsActive: true,
  CreatedByUserID: 1,
  CreatedDate: `2026-10-0${versionNumber}T12:00:00Z`,
  LastModifiedDate: "2026-10-09T12:00:00Z",
  IsLatestVersion: versionNumber === 3,
  document_type_inputs: [],
  document_type_sources: [],
  document_type_sections: [],
});

describe("DocumentTypeVersionRollback", () => {
  it("selects an earlier version, confirms the irreversible versions to discard, and rolls back", async () => {
    const onRollback = jest.fn().mockResolvedValue(undefined);
    render(
      <DocumentTypeVersionRollback
        documentType={version(3, 3, "Draft")}
        versions={[
          version(3, 3, "Draft"),
          version(2, 2, "Published"),
          version(1, 1, "Published"),
        ]}
        onRollback={onRollback}
      />,
    );

    fireEvent.click(screen.getByRole("button", { name: /Version 3, Draft/ }));
    fireEvent.click(
      screen.getByRole("button", { name: /Version 2 · Published/ }),
    );

    expect(onRollback).not.toHaveBeenCalled();
    expect(screen.getByText(/IRREVERSIBLE/)).toBeTruthy();
    expect(screen.getAllByText("Version 3 · Draft").length).toBeGreaterThan(0);
    fireEvent.click(screen.getByRole("button", { name: "Roll back" }));

    await waitFor(() => expect(onRollback).toHaveBeenCalledWith(2));
    expect(onRollback).toHaveBeenCalledTimes(1);
  });

  it("offers version selection for a published version with history and hides it without prior versions", () => {
    const onRollback = jest.fn();
    const { rerender, container } = render(
      <DocumentTypeVersionRollback
        documentType={version(2, 2, "Published")}
        versions={[version(2, 2, "Published"), version(1, 1, "Published")]}
        onRollback={onRollback}
      />,
    );
    expect(
      screen.getByRole("button", { name: /Version 2, Published/ }),
    ).toBeTruthy();

    rerender(
      <DocumentTypeVersionRollback
        documentType={version(1, 1, "Published")}
        versions={[version(1, 1, "Published")]}
        onRollback={onRollback}
      />,
    );
    expect(container.innerHTML).toBe("");
  });

  it("surfaces a failed versions load instead of hiding rollback silently", () => {
    render(
      <DocumentTypeVersionRollback
        documentType={version(3, 3, "Draft")}
        versions={null}
        loadError="Could not load document type versions. Refresh and try again."
        onRollback={jest.fn()}
      />,
    );

    expect(screen.getByRole("alert").textContent).toBe(
      "Could not load document type versions. Refresh and try again.",
    );
    expect(
      screen.queryByRole("button", { name: /Select an earlier version/ }),
    ).toBeNull();
  });

  it("does not call rollback when a version is selected or the confirmation is cancelled", () => {
    const onRollback = jest.fn();
    render(
      <DocumentTypeVersionRollback
        documentType={version(3, 3, "Draft")}
        versions={[version(3, 3, "Draft"), version(2, 2, "Published")]}
        onRollback={onRollback}
      />,
    );

    fireEvent.click(screen.getByRole("button", { name: /Version 3, Draft/ }));
    fireEvent.click(
      screen.getByRole("button", { name: /Version 2 · Published/ }),
    );
    expect(onRollback).not.toHaveBeenCalled();

    fireEvent.click(screen.getByRole("button", { name: "Cancel" }));
    expect(onRollback).not.toHaveBeenCalled();
    expect(screen.queryByText(/IRREVERSIBLE/)).toBeNull();
  });

  it("shows a failed rollback request and keeps the current version selected", async () => {
    const onRollback = jest
      .fn()
      .mockRejectedValue(new Error("rollback failed"));
    render(
      <DocumentTypeVersionRollback
        documentType={version(3, 3, "Draft")}
        versions={[version(3, 3, "Draft"), version(2, 2, "Published")]}
        onRollback={onRollback}
      />,
    );

    fireEvent.click(screen.getByRole("button", { name: /Version 3, Draft/ }));
    fireEvent.click(
      screen.getByRole("button", { name: /Version 2 · Published/ }),
    );
    fireEvent.click(screen.getByRole("button", { name: "Roll back" }));

    expect((await screen.findByRole("alert")).textContent).toContain(
      "rollback failed",
    );
    expect(onRollback).toHaveBeenCalledTimes(1);
    expect(screen.getByText(/IRREVERSIBLE/)).toBeTruthy();
    expect(screen.getByRole("button", { name: "Roll back" })).toBeTruthy();
  });
});
